<template>
  <div>
    <div v-if="isFetching">
      กำลังดึงข้อมูล ...
    </div>
    <div
      v-else-if="setting?.shopBillingInfo?.status === 'PENDING' || isFinished"
      class="p-6 min-h-60 flex flex-col gap-6 justify-center items-center bg-white">
      <v-icon
        color="primary"
        size="100">
        mdi-file-clock-outline
      </v-icon>
      <span class="font-medium text-2xl">กำลังตรวจสอบข้อมูล หากต้องการสอบถามข้อมูลเพิ่มเติม กรุณาติดต่อ LINE @shipnity</span>
    </div>
    <v-form
      v-else
      ref="form"
      v-model="valid"
      :readonly="form.isSubmitting"
      class="flex flex-col gap-3"
      lazy-validation
      @submit.prevent="submit()">
      <div
        v-if="setting?.shopBillingInfo?.status === 'APPROVED'"
        class="p-6 rounded-lg shadow-md flex flex-col gap-6 justify-center items-center bg-white">
        <v-icon
          color="success"
          size="100">
          mdi-check-circle-outline
        </v-icon>
        <span class="font-medium text-2xl">อนุมัติการเปิด COD</span>
      </div>
      <div
        v-if="setting?.shopBillingInfo?.status === 'REJECTED'"
        class="p-6 rounded-lg shadow-md flex flex-col gap-6 justify-center items-center bg-white">
        <v-icon
          color="error"
          size="100">
          mdi-close-circle-outline
        </v-icon>
        <span class="font-medium text-2xl">ไม่อนุมัติการเปิด COD เนื่องจาก {{ setting?.shopBillingInfo?.rejectedReason }}</span>
      </div>
      <v-radio-group
        :disabled="setting?.shopBillingInfo?.status === 'APPROVED'"
        :value="form.isCompany"
        row
        hide-details
        @change="selectIsCompany($event)">
        <v-radio
          label="บุคคลธรรมดา"
          :value="false" />
        <v-radio
          label="นิติบุคคล"
          :value="true" />
      </v-radio-group>
      <div
        v-if="form.isCompany"
        class="text-lg font-medium text-error px-6 py-3 flex flex-col items-center gap-2 shadow-sm rounded-lg">
        ชื่อบัญชีธนาคารกับชื่อในหนังสือรับรองต้องตรงกัน
      </div>
      <div
        v-else
        class="text-lg font-medium text-error px-6 py-3 flex flex-col items-center gap-2 shadow-sm rounded-lg">
        ชื่อบัญชีธนาคารกับชื่อในบัตรประชาชนต้องตรงกัน
      </div>
      <div class="px-6 py-3 flex flex-col items-center gap-2 shadow-md rounded-lg">
        <span class="text-xl font-medium text-primary self-start">เบอร์โทรศัพท์</span>
        <div class="flex flex-col">
          <div class="flex gap-1 items-center">
            <text-field-label
              label="เบอร์โทรศัพท์">
            </text-field-label>
            <span class="text-error">*</span>
          </div>
          <v-text-field
            v-model="form.tel"
            :disabled="setting?.shopBillingInfo?.status === 'APPROVED'"
            :rules="rule.telephone"
            placeholder="เบอร์โทรศัพท์"
            dense
            outlined
            required />
        </div>
      </div>
      <div
        class="px-6 pt-3 pb-6 flex flex-col items-center gap-2 shadow-md rounded-lg">
        <span class="text-xl font-medium text-primary self-start">บัญชีธนาคาร</span>
        <div
          v-if="setting?.shopBillingInfo?.status !== 'APPROVED'"
          class="pb-6 relative flex flex-col">
          <div class="flex gap-1 items-center">
            <text-field-label
              label="หน้าแรกสมุดบัญชีธนาคาร / bookbank" />
            <span class="text-error">*</span>
          </div>
          <upload
            style="width: 300px"
            :show-delete="Boolean(form.bookbankPictureUrl)"
            :value="form.bookbankPictureUrl"
            name="bookbankPicture"
            :confirm="false"
            @remove="removeBookbankPicture"
            @submit="selectBookbankPicture($event.file)" />
          <span
            v-if="Boolean(form.bookbankPictureError)"
            class="absolute bottom-0 text-error">{{ form.bookbankPictureError }}</span>
        </div>
        <div class="w-full 2xl:flex-row flex flex-col gap-4 2xl:items-start">
          <div class="flex flex-col">
            <div class="flex gap-1 items-center">
              <text-field-label
                label="ธนาคาร">
              </text-field-label>
              <span class="text-error">*</span>
            </div>
            <v-select
              v-model="form.bank"
              :disabled="setting?.shopBillingInfo?.status === 'APPROVED'"
              :items="banks"
              item-text="name"
              item-value="value"
              hide-details
              dense
              outlined
              @change="selectBank($event)" />
          </div>
          <div class="flex flex-col">
            <div class="flex gap-1 items-center">
              <text-field-label
                label="เลขบัญชี">
              </text-field-label>
              <span class="text-error">*</span>
            </div>
            <v-text-field
              v-model="form.bankAccount"
              :disabled="setting?.shopBillingInfo?.status === 'APPROVED'"
              :rules="rule.required"
              placeholder="เลขบัญชี"
              dense
              hide-details
              outlined
              required />
          </div>
          <div class="flex flex-col">
            <div class="flex gap-1 items-center">
              <text-field-label
                label="ชื่อบัญชี">
              </text-field-label>
              <span class="text-error">*</span>
            </div>
            <v-text-field
              v-model="form.bankAccountName"
              :disabled="setting?.shopBillingInfo?.status === 'APPROVED'"
              :rules="rule.required"
              placeholder="ชื่อบัญชี"
              dense
              hide-details
              outlined
              required />
          </div>
          <div class="flex flex-col">
            <div class="flex gap-1 items-center">
              <text-field-label
                label="สาขา">
              </text-field-label>
              <span class="text-error">*</span>
            </div>
            <v-text-field
              v-model="form.bankAccountBranch"
              :disabled="setting?.shopBillingInfo?.status === 'APPROVED'"
              :rules="rule.required"
              placeholder="สาขา"
              dense
              hide-details
              outlined
              required />
          </div>
        </div>
      </div>
      <div
        v-if="form.isCompany"
        class="px-6 py-3 flex flex-col items-center gap-2 shadow-md rounded-lg">
        <span class="text-xl font-medium text-primary self-start">หนังสือรับรองบริษัท</span>
        <div
          v-if="setting?.shopBillingInfo?.status !== 'APPROVED'"
          class="pb-6 relative flex flex-col">
          <div class="flex gap-1 items-center">
            <text-field-label
              label="หนังสือรับรองบริษัท" />
            <span class="text-error">*</span>
          </div>
          <upload
            style="width: 300px"
            :show-delete="Boolean(form.companyCertPictureUrl)"
            :value="form.companyCertPictureUrl"
            name="companyCertPicture"
            :confirm="false"
            @remove="removeCompanyCertPicture"
            @submit="selectCompanyCertPicture($event.file)" />
          <span
            v-if="Boolean(form.companyCertPictureError)"
            class="absolute bottom-0 text-error">{{ form.companyCertPictureError }}</span>
        </div>
      </div>
      <div
        v-if="!form.isCompany"
        class="px-6 py-3 flex flex-col items-center gap-2 shadow-md rounded-lg">
        <span class="text-xl font-medium text-primary self-start">รหัสบัตรประชาชน / Passport</span>
        <div
          v-if="setting?.shopBillingInfo?.status !== 'APPROVED'"
          class="flex gap-4 justify-between flex-wrap">
          <div class="pb-6 relative flex flex-col items-center">
            <div class="flex gap-1 items-center">
              <text-field-label
                label="รูปบัตรประจำตัวประชาชน / Passport ด้านหน้า" />
              <span class="text-error">*</span>
            </div>
            <upload
              style="width: 300px"
              :show-delete="Boolean(form.identityCardFrontPictureUrl)"
              :value="form.identityCardFrontPictureUrl"
              name="identityCardFrontPicture"
              :confirm="false"
              @remove="removeIdentityCardFrontPicture"
              @submit="selectIdentityCardFrontPicture($event.file)" />
            <span
              v-if="Boolean(form.identityCardFrontPictureError)"
              class="absolute bottom-0 text-error">{{ form.identityCardFrontPictureError }}</span>
          </div>
          <div class="relative pb-6 flex flex-col items-center">
            <div class="flex gap-1 items-center">
              <text-field-label
                label="รูปคู่บัตรประจำตัวประชาชน / Passport (หน้าแนบบัตร)" />
              <span class="text-error">*</span>
            </div>
            <upload
              style="width: 300px"
              :show-delete="Boolean(form.identityCardSelfiePictureUrl)"
              :value="form.identityCardSelfiePictureUrl"
              name="identityCardSelfiePicture"
              :confirm="false"
              @remove="removeIdentityCardSelfiePicture"
              @submit="selectIdentityCardSelfiePicture($event.file)" />
            <span
              v-if="Boolean(form.identityCardSelfiePictureError)"
              class="absolute bottom-0 text-error">{{ form.identityCardSelfiePictureError }}</span>
          </div>
        </div>
        <div class="flex flex-col">
          <div class="flex gap-1 items-center">
            <text-field-label
              label="บัตรประชาชน / Passport" />
            <span class="text-error">*</span>
          </div>
          <v-text-field
            v-model="form.taxId"
            :disabled="setting?.shopBillingInfo?.status === 'APPROVED'"
            :rules="rule.taxId"
            placeholder="บัตรประชาชน / Passport"
            dense
            outlined
            required />
        </div>
      </div>
      <div
        class="px-6 py-3 flex flex-col gap-2 shadow-md rounded-lg">
        <span class="text-xl font-medium text-primary self-start">ที่อยู่</span>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div class="flex flex-col">
            <div class="flex gap-1 items-center">
              <text-field-label
                label="ที่อยู่" />
              <span class="text-error">*</span>
            </div>
            <v-text-field
              v-model="form.address"
              :disabled="setting?.shopBillingInfo?.status === 'APPROVED'"
              :rules="rule.required"
              placeholder="ที่อยู่"
              dense
              outlined
              required />
          </div>
          <div class="flex flex-col">
            <div class="flex gap-1 items-center">
              <text-field-label
                label="เขต / ตำบล" />
              <span class="text-error">*</span>
            </div>
            <v-text-field
              v-model="form.subdistrict"
              :disabled="setting?.shopBillingInfo?.status === 'APPROVED'"
              :rules="rule.required"
              placeholder="เขต / ตำบล"
              dense
              outlined
              required />
          </div>
          <div class="flex flex-col">
            <div class="flex gap-1 items-center">
              <text-field-label
                label="อำเภอ" />
              <span class="text-error">*</span>
            </div>
            <v-text-field
              v-model="form.district"
              :disabled="setting?.shopBillingInfo?.status === 'APPROVED'"
              :rules="rule.required"
              placeholder="อำเภอ"
              dense
              outlined
              required />
          </div>
          <div class="flex flex-col">
            <div class="flex gap-1 items-center">
              <text-field-label
                label="จังหวัด" />
              <span class="text-error">*</span>
            </div>
            <v-text-field
              v-model="form.province"
              :disabled="setting?.shopBillingInfo?.status === 'APPROVED'"
              :rules="rule.required"
              placeholder="จังหวัด"
              dense
              outlined
              required />
          </div>
          <div class="flex flex-col self-start">
            <div class="flex gap-1 items-center">
              <text-field-label
                label="รหัสไปรษณีย์" />
              <span class="text-error">*</span>
            </div>
            <snt-number-field
              v-model="form.postcode"
              :disabled="setting?.shopBillingInfo?.status === 'APPROVED'"
              :min="0"
              :rules="rule.required"
              rounded />
          </div>
        </div>
      </div>
      <save-change-button
        v-if="setting?.shopBillingInfo?.status !== 'APPROVED'"
        class="self-end"
        :disabled="form.isSubmitting || isFetching"
        @click="submit()">
      </save-change-button>
      <v-form />
    </v-form>
  </div>
</template>

<script>
import Upload from '@/components/Form/Upload.vue'
import { UPDATE_BILLING_INFO } from '@/resources/graphql/mutation/Setting'
import { GET_SETTING_SHOP_BILLING_INFO } from '@/resources/graphql/query/Setting'

export default {
  components: {
    Upload
  },
  apollo: {
    setting: () => ({
      query: GET_SETTING_SHOP_BILLING_INFO,
      watchLoading (isLoading) {
        this.isFetching = isLoading
      },
      result ({ data }) {
        const shopBillingInfo = data.setting.shopBillingInfo
        if (!shopBillingInfo) {
          throw new Error('shop billing info not found')
        }

        const isApproved = data.setting.shopBillingInfo.status === 'APPROVED'
        this.form.isCompany = shopBillingInfo.isCompany
        this.form.tel = isApproved ? this.getHiddenText(shopBillingInfo.tel) : shopBillingInfo.tel
        this.form.bookbankPictureUrl = isApproved ? undefined : shopBillingInfo.bookBankUrl
        this.form.bankAccount = isApproved ? this.getHiddenText(shopBillingInfo.bankAccount) : shopBillingInfo.bankAccount
        this.form.bankAccountName = isApproved ? this.getHiddenText(shopBillingInfo.bankAccountName) : shopBillingInfo.bankAccountName
        this.form.bank = this.banks.find((bank) => bank.value === shopBillingInfo.bankName)
        this.form.bankAccountBranch = isApproved ? this.getHiddenText(shopBillingInfo.bankAccountBranch) : shopBillingInfo.bankAccountBranch
        this.form.identityCardFrontPictureUrl = isApproved ? undefined : shopBillingInfo.identityCardFrontUrl
        this.form.identityCardSelfiePictureUrl = isApproved ? undefined : shopBillingInfo.identityCardSelfieUrl
        this.form.companyCertPictureUrl = isApproved ? undefined : shopBillingInfo.identityCardFrontUrl
        this.form.taxId = isApproved ? this.getHiddenText(shopBillingInfo.taxId) : shopBillingInfo.taxId
        this.form.address = isApproved ? this.getHiddenText(shopBillingInfo.address) : shopBillingInfo.address
        this.form.district = isApproved ? this.getHiddenText(shopBillingInfo.district) : shopBillingInfo.district
        this.form.province = isApproved ? this.getHiddenText(shopBillingInfo.province) : shopBillingInfo.province
        this.form.subdistrict = isApproved ? this.getHiddenText(shopBillingInfo.subdistrict) : shopBillingInfo.subdistrict
        this.form.postcode = shopBillingInfo.postcode
      }
    })
  },
  data () {
    return {
      isFinished: false,
      isFetching: false,
      banks: [{ name: 'กสิกร', value: 'KBANK' },
        { name: 'ไทยพาณิชย์', value: 'SCB' },
        { name: 'กรุงเทพ', value: 'BBL' },
        { name: 'กรุงไทย', value: 'KTB' },
        { name: 'TMB', value: 'TMB' },
        { name: 'กรุงศรี', value: 'BAY' }
      ],
      valid: true,
      form: {
        isSubmitting: false,
        isCompany: false,
        address: '',
        subdistrict: '',
        district: '',
        province: '',
        postcode: '',
        tel: '',
        bookbankPicture: undefined,
        bookbankPictureUrl: undefined,
        bookbankPictureError: '',
        bankAccount: '',
        bankAccountName: '',
        bank: { name: 'กสิกร', value: 'KBank' },
        bankAccountBranch: '',
        identityCardFrontPicture: undefined,
        identityCardFrontPictureUrl: undefined,
        identityCardFrontPictureError: '',
        identityCardSelfiePicture: undefined,
        identityCardSelfiePictureUrl: undefined,
        identityCardSelfiePictureError: '',
        companyCertPicture: undefined,
        companyCertPictureUrl: undefined,
        companyCertPictureError: '',
        taxId: ''
      },
      rule: {
        required: [(v) => !!v || this.$t('app.validate.required')],
        taxId: [
          (v) => !!v || this.$t('app.validate.required'),
          (v) => (v && v.length <= 24) || this.$t('app.validate.min', [24])
        ],
        telephone: [
          (v) => !!v || 'เบอร์โทรศัพท์ไม่ถูกต้อง',
          (v) => {
            const re = /^0\d{8,}$/
            return re.test(v) || 'เบอร์โทรศัพท์ไม่ถูกต้อง'
          }
        ]
      }
    }
  },
  methods: {
    async submit () {
      if (!this.form.bookbankPictureUrl) {
        this.form.bookbankPictureError = 'กรุณาใส่รูปภาพ'

        return
      }

      if (this.$refs.form.validate()) {
        if (this.form.isCompany) {
          if (!this.form.companyCertPictureUrl) {
            this.form.companyCertPictureError = 'กรุณาใส่รูปภาพ'

            return
          }
        } else {
          if (!this.form.identityCardFrontPictureUrl) {
            this.form.identityCardFrontPictureError = 'กรุณาใส่รูปภาพ'

            return
          }
          if (!this.form.identityCardSelfiePictureUrl) {
            this.form.identityCardSelfiePictureError = 'กรุณาใส่รูปภาพ'

            return
          }
        }

        this.form.isSubmitting = true
        const res = await this.$apollo.mutate({
          mutation: UPDATE_BILLING_INFO,
          variables: {
            input: {
              isCompany: this.form.isCompany,
              tel: this.form.tel,
              bookBank: this.form.bookbankPicture,
              bankName: this.form.bank.value,
              bankAccount: this.form.bankAccount,
              bankAccountName: this.form.bankAccountName,
              bankAccountBranch: this.form.bankAccountBranch,
              identityCardFront: this.form.isCompany ? this.form.companyCertPicture : this.form.identityCardFrontPicture,
              identityCardSelfie: this.form.isCompany ? undefined : this.form.identityCardSelfiePicture,
              taxId: this.form.isCompany ? undefined : this.form.taxId,
              address: this.form.address,
              district: this.form.district,
              subdistrict: this.form.subdistrict,
              postcode: this.form.postcode.toString(),
              province: this.form.province
            }
          }
        })

        const error = res.errors || res?.data?.updateBillingInfo?.errors?.length > 0
        if (!error) {
          this.isFinished = true
        }

        this.form.isSubmitting = false
      }
    },
    getHiddenText (text) {
      if (typeof text === 'string') {
        let res = ''
        for (let i = 0; i < text.length; i++) {
          if (i < Math.floor(text.length * (1 / 3)) || i > text.length * (2 / 3)) {
            res += text[i]
          } else {
            res += '*'
          }
        }
        return res
      }

      return text
    },
    selectIsCompany (value) {
      this.form.isCompany = value
    },
    selectIdentityCardFrontPicture (file) {
      this.form.identityCardFrontPictureError = ''
      this.form.identityCardFrontPicture = file
      this.form.identityCardFrontPictureUrl = URL.createObjectURL(file)
    },
    removeIdentityCardFrontPicture () {
      this.form.identityCardFrontPicture = undefined
      this.form.identityCardFrontPictureUrl = undefined
    },
    selectIdentityCardSelfiePicture (file) {
      this.form.identityCardSelfiePictureError = ''
      this.form.identityCardSelfiePicture = file
      this.form.identityCardSelfiePictureUrl = URL.createObjectURL(file)
    },
    removeIdentityCardSelfiePicture () {
      this.form.identityCardSelfiePicture = undefined
      this.form.identityCardSelfiePictureUrl = undefined
    },
    selectBookbankPicture (file) {
      this.form.bookbankPictureError = ''
      this.form.bookbankPicture = file
      this.form.bookbankPictureUrl = URL.createObjectURL(file)
    },
    removeBookbankPicture () {
      this.form.bookbankPicture = undefined
      this.form.bookbankPictureUrl = undefined
    },
    selectCompanyCertPicture (file) {
      this.form.companyCertPictureError = ''
      this.form.companyCertPicture = file
      this.form.companyCertPictureUrl = URL.createObjectURL(file)
    },
    removeCompanyCertPicture () {
      this.form.companyCertPicture = undefined
      this.form.companyCertPictureUrl = undefined
    },
    selectBank (bank) {
      this.form.bank = this.banks.find((b) => b.value === bank)
    }
  }
}
</script>
